import { Component, OnInit } from '@angular/core';
import { Validators, UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { AppConstants } from 'src/app/app.constants';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { concatMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ApiService } from 'src/app/api.service';
import { FunctionsService } from 'src/app/services/functions.service';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { DateAdapter } from '@angular/material/core';
import { Router } from '@angular/router';
import { OkHtmlModalComponent } from 'src/app/generic-components/ok-html-modal/ok-html-modal.component';
import { ConfirmationModalComponent } from 'src/app/generic-components/confirmation-modal/confirmation-modal.component';

@Component({
  selector: 'app-add-user',
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.css'],
  providers: [NgxSpinnerService]
})
export class AddUserComponent implements OnInit {
  formSubmitted = false;
  fieldGroup: any = {};
  profilePicture: File;
  picturePreview: any;
  dataSource: any;
  fieldOption = false;
  currentDate = new Date(new Date().getFullYear() + '-' + (new Date().getMonth() + 1) + '-' + new Date().getDate());
  minDate = new Date((new Date().getFullYear() - 118) + '-01-01T00:00:00');
  dobDate: Date;
  lang = localStorage.getItem('language') || 'en';
  smsConsent = false;
  serverError = '';

  addUserForm = this.formBuilder.group({
    firstName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    lastName: ['', [Validators.required, Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    phoneNumber: ['', AppConstants.phoneRegExReq],
    phoneNumberResidence: ['', AppConstants.phoneRegEx],
    email: ['', [Validators.required, Validators.maxLength(320), Validators.pattern(AppConstants.emailRegex)]],
    role: ['', Validators.required],
    motorVehicle: [''],
    children: [null],
    odim: [''],
    sendSms: [false],
    dob: [''],
    gender: [''],
    city: ['', [Validators.maxLength(60), Validators.pattern(AppConstants.addressRegex)]],
    primaryContactName: ['', [Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    primaryContactPhoneNumber: ['', AppConstants.phoneRegEx],
    secondaryContactName: ['', [Validators.maxLength(50), Validators.pattern(AppConstants.nameRegEx)]],
    secondaryContactPhoneNumber: ['', AppConstants.phoneRegEx],
    state: ['', [Validators.maxLength(60), Validators.pattern(AppConstants.addressRegex)]],
    address: ['', [Validators.maxLength(150), Validators.pattern(AppConstants.addressRegex)]],
    country: ['', [Validators.maxLength(60), Validators.pattern(AppConstants.addressRegex)]],
    zip: new UntypedFormControl('', [Validators.maxLength(5), Validators.pattern(AppConstants.numberRegex)])
  });

  constructor(
    private formBuilder: UntypedFormBuilder,
    private modalService: NgbModal,
    public functionsService: FunctionsService,
    private spinner: NgxSpinnerService,
    private apiService: ApiService,
    public translate: TranslateService,
    private router: Router,
    private dateAdapter: DateAdapter<Date>
  ) {
    this.functionsService.setTitle('user.add_user');
  }

  ngOnInit() {
    this.dateAdapter.setLocale(this.lang);
    this.fetchCustomFields();
    this.getUserFieldOption();
    this.translate.onLangChange.subscribe((event) => {
      this.lang = event.lang;
      this.dateAdapter.setLocale(this.lang);
    });
  }

  private fetchCustomFields() {
    this.spinner.show();
    this.apiService.getCustomFields().subscribe(resp => {
      const jsonObject = resp as any[];
      if (jsonObject != null) {
        jsonObject.forEach(field => {
          this.fieldGroup[field['id'].toString()] = new UntypedFormControl('');
        });
        this.addUserForm.addControl('custom', new UntypedFormGroup(this.fieldGroup));
        this.dataSource = jsonObject;
      }
      this.spinner.hide();
    }, error => {
      this.spinner.hide();
      this.functionsService.errorHandler(error['status']);
    });
  }

  addUserSave() {
    this.serverError = '';
    this.addUserForm.markAllAsTouched();
    if (!this.addUserForm.valid) {
      this.formSubmitted = true;
      return;
    }
    const confirmModalRef = this.modalService.open(ConfirmationModalComponent);
    confirmModalRef.componentInstance.title = this.translate.instant('user.add_user');
    confirmModalRef.componentInstance.message = this.translate.instant('user.add_user_prompt');
    confirmModalRef.result.then(confirmed => {
      if (confirmed) {
        this.spinner.show();
        Object.keys(this.addUserForm.controls).forEach((key) => {
          if (key !== 'dob' && key !== 'custom') {
            const val = this.addUserForm.value[key];
            this.addUserForm.value[key] = (typeof val === 'boolean') ? val : (val ? val.trim() : '');
          }
        });
        const bd = this.addUserForm.value['dob'] ? this.functionsService.formatDate({ value: this.addUserForm.value['dob'] }) : null;
        this.addUserForm.value['dob'] = bd;
        this.apiService.createUserProfile(this.addUserForm.value).pipe(
          concatMap(result => {
            if (this.profilePicture) {
              return this.apiService.uploadProfilePicture(Number(result['id']), this.profilePicture);
            }
            if (result['changedFields']) {
              let fields = '';
              result['changedFields'].forEach(element => {
                const name = this.dataSource.find(o => o.id == element);
                fields += '<li>' + name.field + '</li><br>';
              });
              const modalRef = this.modalService.open(OkHtmlModalComponent, { size: 'lg', windowClass: 'modal' });
              modalRef.componentInstance.title = this.translate.instant('created_user');
              modalRef.componentInstance.message = this.translate.instant('fields_changed_create') + '<br><ol>' + fields + '</ol>';
              modalRef.result.then(() => modalRef.close());
            }
            return of(0);
          })
        ).subscribe(
          () => {
            this.spinner.hide();
            this.router.navigateByUrl('/main/users');
          },
          error => {
            this.spinner.hide();
            if (error['error']['content'] != null) {
              this.serverError = this.translate.instant(error['error']['content']);
              this.translate.onLangChange.subscribe(() => {
                if (this.serverError) {
                  this.serverError = this.translate.instant(error['error']['content']);
                }
              });
            } else {
              this.functionsService.errorHandler(error['status']);
            }
          }
        );
      }
    });
  }

  handleProfilePictureUpload(files: FileList) {
    this.serverError = '';
    if (files.item(0).size > 5000000) {
      this.serverError = this.translate.instant('profile.img_upload_size_err');
      this.translate.onLangChange.subscribe(() => {
        this.serverError = this.translate.instant('profile.img_upload_size_err');
      });
    } else if (AppConstants.imageFormats.includes(files.item(0).type)) {
      this.profilePicture = files.item(0);
      const reader = new FileReader();
      reader.onload = () => this.picturePreview = reader.result;
      reader.readAsDataURL(this.profilePicture);
    } else {
      this.serverError = this.translate.instant('error.image_format');
      this.translate.onLangChange.subscribe(() => {
        this.serverError = this.translate.instant('error.image_format');
      });
    }
  }

  clearDobDate() {
    this.dobDate = null;
  }

  getUserFieldOption() {
    this.spinner.show();
    this.apiService.getUserFieldOption().subscribe(
      data => {
        this.fieldOption = data['userFieldOption'] === 'YES';
        this.spinner.hide();
      },
      error => {
        this.fieldOption = false;
        this.spinner.hide();
      }
    );
  }

  isFieldInvalid(fieldName: string): boolean {
    const control = this.addUserForm.get(fieldName);
    return control && control.invalid && (this.formSubmitted || control.touched || control.dirty);
  }

  isFieldValid(fieldName: string): boolean {
    const control = this.addUserForm.get(fieldName);
    return control && control.valid && (control.touched || control.dirty);
  }

  hasError(fieldName: string, errorType: string): boolean {
    const control = this.addUserForm.get(fieldName);
    return control && control.hasError(errorType);
  }
}
